import Functions from './functions';
import CONSTANTS from '@/constants/index.js';
import { AudioContext } from 'standardized-audio-context';

export default class Audio {
  constructor() {
    this.audioContext = null;
    this.audioBuffers = [];
    this.time = new Date();
    this.droneSourceNodes = [];
    this.agentGainer = {};
    this.droneGainer = {};
    this.fadeInOutDuration = 7;

    // lineGraph関係
    this.lineGraphAnalyser = null;
    this.lineGraphBufferLength = null;
    this.lineGraphDataArray = [];

    // barGraph関係
    this.barGraphAnalyser = null;
    this.barGraphBufferLength = null;
    this.barGraphDataArray = [];

    // eclipse関係
    this.eclipseGraphAnalyser = null;
    this.eclipseGraphBufferLength = null;
    this.eclipseGraphDataArray = [];

    try {
      window.AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      this.barGraphAnalyser = this.audioContext.createAnalyser();

      // 全体のgainを司るgainer
      this.masterGainer = this.audioContext.createGain();
      this.agentGainer = this.audioContext.createGain();
      this.droneGainer = this.audioContext.createGain();

      // lineGraphの描画時の詳細設定もここでしてしまう
      this.lineGraphAnalyser = this.audioContext.createAnalyser();
      this.lineGraphAnalyser.fftSize = 2048;
      this.lineGraphBufferLength = this.lineGraphAnalyser.frequencyBinCount;
      this.lineGraphDataArray = new Uint8Array(this.lineGraphBufferLength);

      // barGraphの描写時の詳細設定もここでしてしまう
      this.barGraphAnalyser.smoothingTimeConstant = 0.8;
      this.barGraphAnalyser.maxDecibels = 0;
      this.barGraphAnalyser.minDecibels = -100;
      this.barGraphAnalyser.fftSize = 512;
      this.barGraphBufferLength = this.barGraphAnalyser.frequencyBinCount;
      this.barGraphDataArray = new Uint8Array(this.barGraphBufferLength);

      // eclipseの描画時の詳細設定もここでしてしまう
      this.eclipseGraphAnalyser = this.audioContext.createAnalyser();
      this.eclipseGraphAnalyser.fftSize = 2048;
      this.eclipseGraphBufferLength = this.eclipseGraphAnalyser.frequencyBinCount;
      this.eclipseGraphDataArray = new Uint8Array(this.eclipseGraphBufferLength);
    } catch (e) {
      alert('Sorry, Web Audio API is not supported in this browser', e);
      return null;
    }
  }

  agentGainerControl = (agentGainValue) => {
    // tbで音が割れるので対策
    if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
      this.agentGainer.gain.value = agentGainValue * 0.5;

      return;
    }

    this.agentGainer.gain.value = agentGainValue;
  };

  droneGainerControl = (droneGainValue) => {
    // tbで音が割れるので対策
    if (window.innerWidth < CONSTANTS.TB_BREAK_POINT) {
      this.droneGainer.gain.value = droneGainValue * 0.5;

      return;
    }

    this.droneGainer.gain.value = droneGainValue;
  };

  execLoad = (sound) => {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.responseType = 'arraybuffer';

      request.open('GET', sound.soundSource, true);

      // safari対策で古いdecodeAudioDataの書き方で実装
      request.onload = () => {
        this.audioContext.decodeAudioData(
          request.response,
          (buffer) => {
            this.audioBuffers[sound.soundName] = buffer;
            resolve();
          },
          (e) => {
            alert('sound load error', e);
            reject();
          }
        );
      };

      request.send();
    });
  };

  loadSoundAsync = async (sounds) => {
    await Promise.all(
      sounds.map(async (sound) => {
        await this.execLoad(sound);
      })
    );
  };

  resume = () => {
    this.audioContext.resume();
  };

  suspend = () => {
    this.audioContext.suspend();
  };

  // agent用の再生メソッド。sourceは再生時間終了後、自動でガベージコレクションされるので、コンストラクターにsourceNodeを持たせない。
  playAgentSound(name, gainValue, panValue) {
    if (!this.audioBuffers[name]) {
      alert(`エラー: ${name}の音源が見つかりません。`);
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffers[name];

    // gain設定
    const gainer = this.audioContext.createGain();
    gainer.gain.value = gainValue;

    // pan設定 (safariやiOS safariのために、createStereoPannerがなければ3Dのpan処理を無理やり2D使用にしている)
    let panner = {};
    if (this.audioContext.createStereoPanner) {
      panner = this.audioContext.createStereoPanner();
      panner.pan.value = panValue;
    } else {
      panner = this.audioContext.createPanner();
      panner.panningModel = 'equalpower';
      panner.setPosition(panValue, 0, 1 - Math.abs(panValue));
    }

    source
      .connect(gainer)
      .connect(panner)
      .connect(this.agentGainer)
      .connect(this.barGraphAnalyser)
      .connect(this.lineGraphAnalyser)
      .connect(this.eclipseGraphAnalyser)
      .connect(this.audioContext.destination);

    source.loop = false;
    source.start(0);
  }

  // agent用の再生メソッド。sourceは再生時間終了後、自動でガベージコレクションされるので、コンストラクターにsourceNodeを持たせない。
  playAgentSoundLinearRampVer(name, gainValue, panValue) {
    if (!this.audioBuffers[name]) {
      alert(`エラー: ${name}の音源が見つかりません。`);
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffers[name];

    // gain設定
    const gainer = this.audioContext.createGain();

    // pan設定 (safariやiOS safariのために、createStereoPannerがなければ3Dのpan処理を無理やり2D使用にしている)
    let panner = {};
    if (this.audioContext.createStereoPanner) {
      panner = this.audioContext.createStereoPanner();
      panner.pan.value = panValue;
    } else {
      panner = this.audioContext.createPanner();
      panner.panningModel = 'equalpower';
      panner.setPosition(panValue, 0, 1 - Math.abs(panValue));
    }

    source
      .connect(gainer)
      .connect(panner)
      .connect(this.agentGainer)
      .connect(this.barGraphAnalyser)
      .connect(this.lineGraphAnalyser)
      .connect(this.eclipseGraphAnalyser)
      .connect(this.audioContext.destination);

    source.loop = false;
    source.start(this.audioContext.currentTime);

    // 0.1秒でgainを指定した値にしていく処理
    gainer.gain.linearRampToValueAtTime(0, this.audioContext.currentTime);
    gainer.gain.linearRampToValueAtTime(gainValue, this.audioContext.currentTime + 0.1);

    // 音源は2秒なので、1.9秒から0.1秒でフェードアウトする処理
    gainer.gain.linearRampToValueAtTime(gainValue, this.audioContext.currentTime + 1.9);
    gainer.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 2);
  }

  // drone用の再生メソッド。sourceをstopさせる関係上、コンストラクターにsourceNodeを保持させてstartさせる。
  playDroneSound(name, gainValue, offset, isLoop) {
    if (!this.audioBuffers[name]) {
      alert(`エラー: ${name}の音源が見つかりません。`);
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffers[name];

    // gain設定
    const gainer = this.audioContext.createGain();
    gainer.gain.value = gainValue;

    source
      .connect(gainer)
      .connect(this.droneGainer)
      .connect(this.barGraphAnalyser)
      .connect(this.lineGraphAnalyser)
      .connect(this.eclipseGraphAnalyser)
      .connect(this.audioContext.destination);

    source.loop = isLoop;
    source.start(0, offset);

    // 停止時に使用するため、保管しとく。
    this.droneSourceNodes.push({
      name,
      sourceNode: source,
    });
  }

  // drone用の再生メソッド。sourceをstopさせる関係上、コンストラクターにsourceNodeを保持させてstartさせる。
  playDroneSoundLinearRampVer(name, gainValue, offset, isLoop) {
    if (!this.audioBuffers[name]) {
      alert(`エラー: ${name}の音源が見つかりません。`);
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = this.audioBuffers[name];

    // gain設定
    const gainer = this.audioContext.createGain();
    gainer.gain.value = 0;

    // フェードインアウト用のgain設定(本当はitem.gainerだけで済む話かと思ったが、droneGainer分のgainも処理しなければならず、同等のgainerも作成して無理やり削る)
    const fadeInOutGainer = this.audioContext.createGain();
    fadeInOutGainer.gain.value = this.droneGainer.gain.value;

    source
      .connect(gainer)
      .connect(fadeInOutGainer)
      .connect(this.droneGainer)
      .connect(this.barGraphAnalyser)
      .connect(this.lineGraphAnalyser)
      .connect(this.eclipseGraphAnalyser)
      .connect(this.audioContext.destination);

    source.loop = isLoop;
    source.start(0, offset);

    gainer.gain.linearRampToValueAtTime(0, this.audioContext.currentTime);
    gainer.gain.linearRampToValueAtTime(gainValue, this.audioContext.currentTime + this.fadeInOutDuration);

    fadeInOutGainer.gain.linearRampToValueAtTime(0, this.audioContext.currentTime);
    fadeInOutGainer.gain.linearRampToValueAtTime(
      fadeInOutGainer.gain.value,
      this.audioContext.currentTime + this.fadeInOutDuration
    );

    // 停止時に使用するため、保管しとく。
    this.droneSourceNodes.push({
      name,
      sourceNode: source,
      gainer,
      fadeInOutGainer,
    });
  }

  // drone用の停止メソッド。
  stopDroneSound(name) {
    const resultArray = this.droneSourceNodes.filter((node) => {
      return node.name === name;
    });

    if (resultArray.length === 0) {
      alert(`エラー: ${name}のsourceNodeが見つかりません。`);
      return;
    }

    resultArray.forEach((item) => {
      item.sourceNode.stop();
    });
  }

  // drone用の停止メソッド（フェードアウトver）。
  stopDroneSoundLinearRampVer(name) {
    const resultArray = this.droneSourceNodes.filter((node) => {
      return node.name === name;
    });

    if (resultArray.length === 0) {
      alert(`エラー: ${name}のsourceNodeが見つかりません。`);
      return;
    }

    resultArray.forEach((item) => {
      item.gainer.gain.linearRampToValueAtTime(item.gainer.gain.value, this.audioContext.currentTime);
      item.gainer.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + this.fadeInOutDuration);

      item.fadeInOutGainer.gain.linearRampToValueAtTime(item.fadeInOutGainer.gain.value, this.audioContext.currentTime);
      item.fadeInOutGainer.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + this.fadeInOutDuration);

      item.sourceNode.stop(this.audioContext.currentTime + this.fadeInOutDuration);
    });
  }

  renderLineGraphAnalyser(canvasContext, backgroundColor) {
    // canvas初期化
    canvasContext.fillStyle = backgroundColor;
    canvasContext.fillRect(0, 0, canvasContext.canvas.clientWidth, canvasContext.canvas.clientHeight);

    this.lineGraphAnalyser.getByteTimeDomainData(this.lineGraphDataArray);

    canvasContext.lineWidth = 1;
    canvasContext.strokeStyle = 'rgb(7, 7, 81)';

    const sliceWidth = (canvasContext.canvas.clientWidth * 1.0) / this.lineGraphBufferLength;
    let x = 0;

    canvasContext.beginPath();

    for (let i = 0; i < this.lineGraphBufferLength; i++) {
      // getByteTimeDomainDataを実行した場合、128が一つの基礎値になるので、vは比率に近いような認識になる。1が基礎値。
      const v = this.lineGraphDataArray[i] / 128.0;
      // TODO: 振れ幅を少し大きくしたいので、vが変動した時に増幅させる
      // const y = v === 1 ? canvasContext.canvas.clientHeight / 2 : (v * v) * canvasContext.canvas.clientHeight / 2;
      const y = (v * canvasContext.canvas.clientHeight) / 2;

      if (i === 0) {
        canvasContext.moveTo(x, y);
      } else {
        canvasContext.lineTo(x, y);
      }

      x += sliceWidth;
    }

    canvasContext.lineTo(canvasContext.canvas.clientWidth, canvasContext.canvas.clientHeight / 2);
    canvasContext.stroke();
  }

  renderBarGraphAnalyser(canvasContext, backgroundColor) {
    // canvas初期化
    canvasContext.fillStyle = backgroundColor;
    canvasContext.fillRect(0, 0, canvasContext.canvas.clientWidth, canvasContext.canvas.clientHeight);

    this.barGraphAnalyser.getByteFrequencyData(this.barGraphDataArray);

    const barWidth = (canvasContext.canvas.clientWidth / this.barGraphBufferLength) * 1.5;

    let barHeight = 0;
    let x = 0;

    for (let i = 0; i < this.barGraphBufferLength; i++) {
      barHeight = this.barGraphDataArray[i];

      canvasContext.fillStyle = 'rgb(7, 7, 81)';
      canvasContext.fillRect(x, canvasContext.canvas.clientHeight - barHeight, barWidth, barHeight);

      x += barWidth + 3;
    }
  }

  renderEclipseGraphAnalyser(canvasContext, backgroundColor, nowRate, isEclipseRetrograde, setIsEclipseRetrograde) {
    // canvas初期化
    canvasContext.fillStyle = backgroundColor;
    canvasContext.fillRect(0, 0, canvasContext.canvas.clientWidth, canvasContext.canvas.clientHeight);

    this.eclipseGraphAnalyser.getByteTimeDomainData(this.eclipseGraphDataArray);

    // 太陽の中心をどこにおくかの計算。結構考え方が複雑なので、以下に記録を残す。
    // https://docs.google.com/spreadsheets/d/1Wzh7h3uwnjEYVFCLEo5ZSNA6uM3bHPMLLGkWzTMzTwM/edit#gid=847191873
    const calculateSunX = (() => {
      const center = canvasContext.canvas.clientWidth / 2;

      let position = 0;

      // 日食へと近づいていく時（順当な方向で進行している時）考え方は上記のメモに。
      if (!isEclipseRetrograde) {
        const startPosition = 0 - (CONSTANTS.SUN_RADIUS + 25); // analyzerの振動で揺れた時を想定して 25多めにとる
        const totalDistance = canvasContext.canvas.clientWidth / 2 + (CONSTANTS.SUN_RADIUS + 25);
        const rate = (nowRate - 0.5) * 2;
        const distanceTraveled = totalDistance * rate;

        position = startPosition + distanceTraveled;

        // 一応画面離脱した時のことを考えハンドリング。真ん中をすぎることはないので、過ぎるのであれば真ん中を返す。
        if (center < position) {
          position = center;
        }
      }

      // 日食から離れていく時(逆の方向で進行している時)考え方は同様上記のメモに。
      if (isEclipseRetrograde) {
        const startPosition = center;
        const totalDistance = canvasContext.canvas.clientWidth / 2 + (CONSTANTS.SUN_RADIUS + 25); // analyzerの振動で揺れた時を想定して 25多めにとる
        const rate = nowRate * 2;
        const distanceTraveled = totalDistance * rate;

        position = startPosition + distanceTraveled;

        const finalDestination = canvasContext.canvas.clientWidth + (CONSTANTS.SUN_RADIUS + 25);

        if (position >= finalDestination) {
          setIsEclipseRetrograde();
        }
      }

      return position;
    })();

    const sunGraphCenter = {
      x: canvasContext.canvas.clientWidth - calculateSunX,
      y: canvasContext.canvas.clientHeight / 2,
    };

    canvasContext.lineWidth = 1;
    canvasContext.strokeStyle = 'rgba(7, 7, 81)';

    canvasContext.beginPath();

    // うまくだたArrayを360で均等に呼び出せないので、randomでぐちゃぐちゃにしてみる。
    for (let i = 0; i < 360; i++) {
      const v = Math.round(Math.random() * this.eclipseGraphBufferLength);
      const r = (this.eclipseGraphDataArray[v] / 128.0) * CONSTANTS.SUN_RADIUS;
      const x = sunGraphCenter.x + r * Math.cos(Functions.g2r(i));
      const y = sunGraphCenter.y + r * Math.sin(Functions.g2r(i));

      if (i === 0) {
        canvasContext.moveTo(x, y);
      } else {
        canvasContext.lineTo(x, y);
      }
    }

    canvasContext.fillStyle = 'rgb(255, 255, 255)';
    canvasContext.fill();
    canvasContext.closePath();
    canvasContext.stroke();

    // 太陽の中心をどこにおくかの計算。結構考え方が複雑なので、以下に記録を残す。
    // https://docs.google.com/spreadsheets/d/1Wzh7h3uwnjEYVFCLEo5ZSNA6uM3bHPMLLGkWzTMzTwM/edit#gid=847191873
    const calculateMoonX = (() => {
      const center = canvasContext.canvas.clientWidth / 2;

      let position = 0;

      // 日食へと近づいていく時（順当な方向で進行している時）考え方は上記のメモに。
      if (!isEclipseRetrograde) {
        const startPosition = 0 - (CONSTANTS.SUN_RADIUS + 25); // analyzerの振動で揺れた時を想定して 25多めにとる;
        const totalDistance = canvasContext.canvas.clientWidth / 2 + (CONSTANTS.MOON_RADIUS + 25);
        const rate = (nowRate - 0.5) * 2;
        const distanceTraveled = totalDistance * rate;

        position = startPosition + distanceTraveled;

        // 一応画面離脱した時のことを考えハンドリング。真ん中をすぎることはないので、過ぎるのであれば真ん中を返す。
        if (center < position) {
          position = center;
        }
      }

      // 日食から離れていく時(逆の方向で進行している時)考え方は同様上記のメモに。
      if (isEclipseRetrograde) {
        const startPosition = center;
        const totalDistance = canvasContext.canvas.clientWidth / 2 + (CONSTANTS.MOON_RADIUS + 25);
        const rate = nowRate * 2;
        const distanceTraveled = totalDistance * rate;

        position = startPosition + distanceTraveled;
      }

      return position;
    })();

    const moonGraphCenter = {
      x: calculateMoonX,
      y: canvasContext.canvas.clientHeight / 2,
    };

    canvasContext.lineWidth = 1;
    canvasContext.strokeStyle = 'rgb(255, 255, 255)';

    canvasContext.beginPath();

    // うまくだたArrayを360で均等に呼び出せないので、randomでぐちゃぐちゃにしてみる。
    for (let i = 0; i < 360; i++) {
      // moonは低周波だけを拾えばいいので0がちょうどいい
      const r = (this.eclipseGraphDataArray[0] / 128.0) * CONSTANTS.MOON_RADIUS;
      const x = moonGraphCenter.x + r * Math.cos(Functions.g2r(i));
      const y = moonGraphCenter.y + r * Math.sin(Functions.g2r(i));

      if (i === 0) {
        canvasContext.moveTo(x, y);
      } else {
        canvasContext.lineTo(x, y);
      }
    }

    canvasContext.fillStyle = 'rgba(7, 7, 81, 0.95)';
    canvasContext.fill();
    canvasContext.closePath();
    canvasContext.stroke();
  }
}
